import {observer} from 'mobx-react-lite';

import {ScrollAnchorWrapper} from 'UI/components/layout/ScrollAnchorWrapper';

import {ApplicationBlock} from 'UI/modules/Blocks/ApplicationBlock';

import {useBlockRenderer} from './useBlockRenderer';
import {hasLinkId, isJobPostingType} from 'Application/utils/typeGuards';

import type {BlockUnionType} from './useBlockRenderer';
import type {PageData} from 'PayloadUI/dataSelectors/getLachenschmidPageData';

/**
 * The `ComponentProps` interface defines the properties for the `BlockRenderer` component.
 * This interface specifies the structure of data required to render dynamic blocks within the component.
 * It includes content blocks, optional metadata, and testing identifiers to ensure proper functionality and testability.
 */
interface ComponentProps {
    /**
     * The array of block data used for rendering dynamic block components.
     * Each item in the array represents a block with its associated data and configuration.
     * If no content is provided (null or undefined), the component will render nothing.
     */
    content: (BlockUnionType)[] | null | undefined;
    /**
     * Optional array of documents and folders that can be used by blocks for additional data.
     * This property provides supplementary content that certain blocks may require for enhanced functionality.
     * When not provided, blocks will operate without access to this additional data source.
     */
    listData?: PageData['documentsAndFolders'];
    /**
     * Optional metadata associated with the page containing the blocks.
     * This property provides contextual information that blocks can utilize for enhanced rendering or behavior.
     * When not provided, blocks will operate with their default metadata handling.
     */
    metaData?: PageData['pageMetaData'];
    /**
     * The unique identifier for testing purposes to ensure reliable component identification during automated tests.
     * This property enables precise targeting of the component in test scenarios and debugging processes.
     * When not specified, it defaults to the component's display name for consistency.
     *
     * @default 'BlockRenderer'
     */
    testId?: string;
}

/**
 * A React component that dynamically renders an array of blocks using their associated components and data.
 * This component receives structured block content (usually from a CMS like Payload) and delegates the rendering
 * of each block to its corresponding React component. It uses the `useBlockRenderer` hook to resolve the mapping
 * between block data and renderable components. Each block is rendered using its unique `id` as a key, and all
 * associated data is spread as props into the block component.
 *
 * @param props         The component props.
 * @param props.content The array of block entries to be rendered, typically containing a `blockType`, `id`, and other data.
 * @returns A rendered list of block components or `null` if no blocks are available.
 *
 * @example
 * ```tsx
 * <BlockRenderer content={page.blocks} />
 * ```
 */
const BlockRenderer = observer(({content, listData, metaData}: ComponentProps) => {
    const {blocks} = useBlockRenderer(content);
    const blockData = blocks ? blocks.map(block => {
        const {Component, data} = block;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return Component ? (
            <ScrollAnchorWrapper
                key={data.id}
                isFirst={data.blockType === 'hero'}
                labelledBy={data.id!}
                linkId={hasLinkId(data) ? `#${data.linkId}` : undefined}
            >
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <Component {...data as any} listData={listData} metaData={metaData} />
            </ScrollAnchorWrapper>
        ) : null;
    }) : null;

    if (isJobPostingType(metaData?.structuredData.listData)) {
        blockData?.push((
            <ScrollAnchorWrapper
                key={metaData.id}
                labelledBy={metaData.id!}
                linkId={undefined}
            >
                <ApplicationBlock id={metaData.id!} />
            </ScrollAnchorWrapper>
        ));
    }

    return blockData;
});

BlockRenderer.displayName = 'BlockRenderer';

export {BlockRenderer};